import { useEffect, useId, useState } from "react";
import { useThemeSlice } from "../ui/StatusBadge.jsx";

const DELETE_COLOR = "#E85D54";

export function DeleteConfirmModal({
  open,
  title = "Delete item",
  description,
  confirmText = "",
  entityLabel = "Name",
  confirmLabel = "Delete",
  onCancel,
  onConfirm,
  busy: busyProp = false,
}) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const inputId = useId();
  const [typed, setTyped] = useState("");
  const [internalBusy, setInternalBusy] = useState(false);
  const busy = busyProp || internalBusy;
  const trimmed = confirmText.trim();
  const canConfirm = trimmed.length > 0 && typed === trimmed && !busy;

  useEffect(() => {
    if (!open) {
      setTyped("");
      setInternalBusy(false);
    }
  }, [open, confirmText]);

  useEffect(() => {
    if (!open || busy) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setInternalBusy(true);
    try {
      await onConfirm?.();
    } finally {
      setInternalBusy(false);
    }
  };

  return (
    <>
      <div
        role="presentation"
        onClick={busy ? undefined : onCancel}
        style={{ position: "fixed", inset: 0, zIndex: 9000, background: dark ? "rgba(0,0,0,0.55)" : "rgba(13,15,18,0.32)", backdropFilter: "blur(6px)" }}
      />
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(480px, calc(100vw - 40px))",
          zIndex: 9001,
          background: c.card,
          border: `1px solid ${c.cardBdr}`,
          borderRadius: 16,
          boxShadow: dark ? "0 24px 64px rgba(0,0,0,.55)" : "0 24px 64px rgba(0,0,0,.12)",
          overflow: "hidden",
        }}
      >
        <div aria-hidden style={{ height: 3, background: `linear-gradient(90deg, ${DELETE_COLOR}, transparent)` }} />
        <div style={{ padding: "20px 24px 22px" }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: c.text }}>{title}</h2>
          {description ? <p style={{ margin: "10px 0 16px", fontSize: 14, color: c.mid, lineHeight: 1.6 }}>{description}</p> : null}
          <div style={{ padding: "12px 14px", borderRadius: 10, border: `1px solid ${DELETE_COLOR}33`, background: `${DELETE_COLOR}0F`, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: DELETE_COLOR, marginBottom: 6 }}>{entityLabel}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: c.text }}>{confirmText || "—"}</div>
          </div>
          <label htmlFor={inputId} style={{ display: "block", fontSize: 13, color: c.mid, marginBottom: 8 }}>
            Type <strong style={{ color: c.text }}>{trimmed || "…"}</strong> below to confirm.
          </label>
          <input
            id={inputId}
            type="text"
            value={typed}
            disabled={busy}
            autoComplete="off"
            autoFocus
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canConfirm) handleConfirm();
            }}
            placeholder={trimmed || "Enter name to confirm"}
            style={{
              width: "100%",
              height: 44,
              borderRadius: 10,
              border: `1px solid ${typed && typed !== trimmed ? DELETE_COLOR : c.cardBdr}`,
              background: c.bg,
              color: c.text,
              padding: "0 14px",
              fontSize: 14,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <button type="button" disabled={busy} onClick={onCancel} style={outlineBtn(c, dark, busy)}>
              Cancel
            </button>
            <button type="button" disabled={!canConfirm} onClick={handleConfirm} style={deleteBtn(canConfirm, busy)}>
              {busy ? "Deleting…" : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function outlineBtn(c, dark, disabled) {
  return {
    height: 44,
    padding: "0 20px",
    borderRadius: 10,
    border: `1px solid ${c.cardBdr}`,
    background: dark ? "rgba(255,255,255,0.03)" : c.card,
    color: c.text,
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 13.5,
    fontWeight: 600,
    opacity: disabled ? 0.5 : 1,
  };
}

function deleteBtn(canConfirm, busy) {
  return {
    height: 44,
    padding: "0 22px",
    borderRadius: 10,
    border: "none",
    background: canConfirm ? DELETE_COLOR : `${DELETE_COLOR}55`,
    color: "#fff",
    cursor: canConfirm ? (busy ? "wait" : "pointer") : "not-allowed",
    fontSize: 13.5,
    fontWeight: 600,
    minWidth: 120,
  };
}
